import { useAddRecentTransaction } from '@rainbow-me/rainbowkit';
import SafeApiKit from '@safe-global/api-kit';
import {
  WaitForTransactionReceiptErrorType,
  WriteContractErrorType,
  WriteContractReturnType,
} from '@wagmi/core';
import { AbiStateMutability, ExtractAbiFunctionNames } from 'abitype';
import { ALERT_SEVERITY, useAlerts } from 'contexts/AlertsContext';
import { useTxStatus } from 'contexts/TxStatusContext';
import { Abi, Address, Client } from 'viem';
import { waitForTransactionReceipt } from 'viem/actions';
import { sepolia } from 'viem/chains';
import { useAccount, useClient, useWriteContract } from 'wagmi';

const pollForTxHash = (safeTxHash: string, chainId?: number): Promise<string> => {
  const apiKit = new SafeApiKit({
    chainId: BigInt(chainId || sepolia.id),
  });

  return new Promise(resolve => {
    const interval = setInterval(async () => {
      const res = await apiKit.getTransaction(safeTxHash)?.catch(err => {
        clearInterval(interval);
        console.error('Safe tx not found: ', err);
      });

      if (res?.transactionHash) {
        clearInterval(interval);
        resolve(res?.transactionHash);
      }
    }, 5000);
  });
};

const waitWithRetryForTxReceipt = async (
  client: Client,
  txHash: `0x${string}`,
  retries = 3,
  timeBetweenRetries = 1000
) => {
  try {
    await waitForTransactionReceipt(client, { hash: txHash });
  } catch (err) {
    if (retries > 0) {
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          waitWithRetryForTxReceipt(client, txHash, retries - 1, timeBetweenRetries)
            ?.then(resolve)
            ?.catch(err => {
              reject(err);
            });
        }, timeBetweenRetries);
      });
    }

    if (retries <= 0) {
      return Promise.reject(err);
    }
  }
};

// const gasOverrideFnMap = window.appConfig?.ui?.gasLimitOverrides || {};

interface UseWriteTxArgs {
  txKey: string;
  contractAbi: Abi;
  contractAddress: Address;
  functionArgs?: unknown[];
  functionName: ExtractAbiFunctionNames<Abi, AbiStateMutability>;
  onError?: (error: WriteContractErrorType | WaitForTransactionReceiptErrorType) => void;
  onTxConfirmed?: (txReceipt: WriteContractReturnType) => void;
}

const getTxLink = (txHash: string, url: string, name: string) => ({
  url: `${url}/tx/${txHash}`,
  label: `View tx on ${name}`,
});

export const useWriteTx = (props: UseWriteTxArgs) => {
  const {
    contractAbi,
    contractAddress,
    functionArgs,
    functionName,
    onError,
    onTxConfirmed,
    txKey,
  } = props;

  const { chain, chainId } = useAccount();
  const { addAlert, removeAlert } = useAlerts();
  const addRecentTransaction = useAddRecentTransaction();

  const { addPendingTx, pendingTxs, removePendingTx } = useTxStatus();
  const isPending = pendingTxs?.has(txKey);

  const client = useClient();
  const {
    data: txHash,
    error,
    isSuccess,
    reset,
    writeContract,
  } = useWriteContract({
    mutation: {
      onSuccess: async txHash => {
        // txHash could be a safeTxHash which will not show up on the block explorer
        addRecentTransaction({ hash: txHash, description: functionName });
        const pendingTxKey = addAlert({
          title: `${functionName} transaction pending`,
          desc: `Tx hash: ${txHash}`,
          severity: ALERT_SEVERITY.PENDING,
          link: getTxLink(
            txHash,
            chain?.blockExplorers?.default.url || '',
            chain?.blockExplorers?.default?.name || ''
          ),
          timeout: Infinity,
        });

        if (client) {
          const underlyingTxHash = await Promise.race([
            waitWithRetryForTxReceipt(client, txHash),
            pollForTxHash(txHash, chainId || 0), // for safe wallet
          ]);

          if (underlyingTxHash) {
            // add gnosis safe tx to recent transactions
            addRecentTransaction({ hash: underlyingTxHash, description: functionName });
          }

          removePendingTx(txKey);
          removeAlert(pendingTxKey);
          addAlert({
            title: `${functionName} transaction confirmed`,
            desc: `Tx hash: ${underlyingTxHash || txHash}`,
            link: getTxLink(
              underlyingTxHash || txHash,
              chain?.blockExplorers?.default.url || '',
              chain?.blockExplorers?.default?.name || ''
            ),
            severity: ALERT_SEVERITY.SUCCESS,
          });
          onTxConfirmed?.(txHash);
        } else {
          console.error('no client configured. Client: ', client);
        }
      },
      onError: err => {
        console.error(`${functionName} transaction failed`, err);
        console.error(
          'Error Message: ',
          err?.message || (err?.cause as string) || 'An unexpected error occured'
        );
        removePendingTx(txKey);
        addAlert({
          title: `${functionName} transaction failed`,
          severity: ALERT_SEVERITY.ERROR,
          desc: err?.message || (err?.cause as string) || 'An unexpected error occured',
          link: txHash
            ? getTxLink(
                txHash,
                chain?.blockExplorers?.default.url || '',
                chain?.blockExplorers?.default?.name || ''
              )
            : undefined,
        });
        onError?.(err);
      },
    },
  });

  const write = () => {
    console.debug(`[${functionName}] function args: `, functionArgs);
    console.debug(`[${functionName}] props: `, props);
    addPendingTx(txKey);
    writeContract({
      abi: contractAbi,
      address: contractAddress,
      functionName,
      args: functionArgs,
    });
  };

  return { write, isSuccess, txHash, isPending, reset, error };
};
